import React from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Globe, Layers, FileText } from 'lucide-react';

const SOURCE_LABEL = {
  job: 'set on this invoice',
  customer: "from the customer's billing currency",
  job_address: 'detected from the job-site address',
  customer_address: "detected from the customer's billing address",
  company: 'company default (Settings → Invoice)',
  default: 'default — no country detected',
};

/** Currency picker (auto vs explicit) + Itemized / Lump-sum presentation. */
export default function InvoiceCurrencyPresentation({
  currency, currencySource, currencyOptions, currencyOverride, setCurrencyOverride,
  presentation, setPresentation, summary, setSummary, jobTitle,
}) {
  const options = currencyOptions?.length ? currencyOptions : ['CAD', 'USD', 'GBP', 'EUR'];
  return (
    <div className="rounded-lg border border-[#2D3748] bg-[#0B131F] p-3 space-y-3" data-testid="invoice-currency-presentation-block">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Billing currency</Label>
          <Select value={currencyOverride || 'auto'} onValueChange={(v) => setCurrencyOverride(v === 'auto' ? '' : v)}>
            <SelectTrigger className="mt-1 bg-[#1A2433] border-[#2D3748] text-white" data-testid="invoice-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto" data-testid="invoice-currency-option-auto">Auto — {currency} ({SOURCE_LABEL[currencySource] || currencySource})</SelectItem>
              {options.map((c) => (
                <SelectItem key={c} value={c} data-testid={`invoice-currency-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">Amounts are denominated in this currency — no exchange-rate conversion is applied.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> Presentation</Label>
          <Select value={presentation} onValueChange={setPresentation}>
            <SelectTrigger className="mt-1 bg-[#1A2433] border-[#2D3748] text-white" data-testid="invoice-presentation-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="itemized" data-testid="invoice-presentation-itemized">Itemized — every line shown</SelectItem>
              <SelectItem value="lump_sum" data-testid="invoice-presentation-lump-sum">Lump sum — total + what's included only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">Lump sum hides labour, materials and charges on the PDF/email; tax and total still show.</p>
        </div>
      </div>
      {presentation === 'lump_sum' && (
        <div>
          <Label htmlFor="invoice-summary" className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> What's included (shown under "{jobTitle || 'Services as quoted'}")</Label>
          <Textarea id="invoice-summary" value={summary} onChange={(e) => setSummary(e.target.value)}
            placeholder="Includes labour, equipment, materials and energy costs for the service."
            className="mt-1 bg-[#1A2433] border-[#2D3748] text-white text-sm min-h-[60px]" data-testid="invoice-summary-input" />
        </div>
      )}
    </div>
  );
}
